import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Brain, ArrowLeft, Trash, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

function BasesList() {
  const { t } = useTranslation();
  const { currentOrg } = useOrg();
  const navigate = useNavigate();
  const [bases, setBases] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });

  const load = () => api.get("/knowledge/bases").then((r) => setBases(r.data)).catch(() => {});
  useEffect(() => { if (currentOrg) load(); }, [currentOrg]);

  const create = async () => {
    try {
      await api.post("/knowledge/bases", form);
      setForm({ name: "", description: "" });
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this knowledge base and all its items?")) return;
    try { await api.delete(`/knowledge/bases/${id}`); load(); }
    catch (er) { toast.error(apiError(er)); }
  };

  return (
    <div>
      <PageHeader
        title={t("knowledge.title")}
        subtitle={t("knowledge.subtitle")}
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm" data-testid="new-kb-button"><Plus size={16} className="mr-2" />{t("knowledge.newBase")}</Button>
            </DialogTrigger>
            <DialogContent className="backdrop-blur-xl bg-background/80">
              <DialogHeader><DialogTitle className="font-heading">{t("knowledge.createBase")}</DialogTitle><DialogDescription className="sr-only">Create a knowledge base</DialogDescription></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2"><Label>{t("common.name")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="kb-name-input" /></div>
                <div className="space-y-2"><Label>{t("knowledge.description")}</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" data-testid="kb-desc-input" /></div>
              </div>
              <DialogFooter><Button onClick={create} disabled={!form.name.trim()} data-testid="kb-create-submit">{t("common.create")}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {bases.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm p-16 text-center text-muted-foreground" data-testid="kb-empty">{t("knowledge.empty")}</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {bases.map((b) => (
            <div
              key={b.id}
              onClick={() => navigate(`/knowledge/${b.id}`)}
              className="bg-card border border-border rounded-sm p-6 cursor-pointer hover:border-primary transition-colors group"
              data-testid={`kb-card-${b.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 bg-secondary flex items-center justify-center rounded-sm group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <Brain size={20} weight="bold" />
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive opacity-0 group-hover:opacity-100" onClick={(e) => remove(b.id, e)} data-testid={`kb-delete-${b.id}`}><Trash size={16} /></Button>
              </div>
              <h3 className="font-heading font-bold text-lg mt-4">{b.name}</h3>
              <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{b.description || "—"}</p>
              <Badge variant="secondary" className="rounded-sm mt-4 text-[10px] font-mono">{b.item_count} {t("knowledge.items")}</Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ItemsList() {
  const { t } = useTranslation();
  const { kbId } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [base, setBase] = useState(null);

  const load = () => {
    api.get(`/knowledge/bases/${kbId}/items`).then((r) => setItems(r.data)).catch(() => {});
    api.get("/knowledge/bases").then((r) => setBase(r.data.find((b) => b.id === kbId))).catch(() => {});
  };
  useEffect(() => { load(); }, [kbId]);

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/knowledge/items/${id}`); load(); }
    catch (er) { toast.error(apiError(er)); }
  };

  return (
    <div>
      <button onClick={() => navigate("/knowledge")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="kb-back-button">
        <ArrowLeft size={16} /> {t("common.back")}
      </button>
      <PageHeader
        title={base?.name || t("knowledge.title")}
        subtitle={base?.description}
        action={<Button className="rounded-sm" onClick={() => navigate(`/knowledge/${kbId}/items/new`)} data-testid="new-item-button"><Plus size={16} className="mr-2" />{t("knowledge.newItem")}</Button>}
      />
      {items.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm p-16 text-center text-muted-foreground" data-testid="items-empty">{t("knowledge.emptyItems")}</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <div key={it.id} onClick={() => navigate(`/knowledge/${kbId}/items/${it.id}`)} className="bg-card border border-border rounded-sm p-5 cursor-pointer hover:border-primary transition-colors group" data-testid={`item-card-${it.id}`}>
              <div className="flex items-start justify-between">
                <FileText size={20} className="text-primary" weight="bold" />
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive opacity-0 group-hover:opacity-100" onClick={(e) => remove(it.id, e)} data-testid={`item-delete-${it.id}`}><Trash size={16} /></Button>
              </div>
              <h4 className="font-heading font-bold mt-3">{it.name}</h4>
              {it.brand && <p className="text-xs text-muted-foreground mt-1">{it.brand}</p>}
              <div className="flex flex-wrap gap-1 mt-3">
                {it.products?.length > 0 && <Badge variant="secondary" className="rounded-sm text-[10px] font-mono">{it.products.length} products</Badge>}
                {it.faqs?.length > 0 && <Badge variant="secondary" className="rounded-sm text-[10px] font-mono">{it.faqs.length} faqs</Badge>}
                {it.documents?.length > 0 && <Badge variant="secondary" className="rounded-sm text-[10px] font-mono">{it.documents.length} docs</Badge>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Knowledge() {
  const { kbId } = useParams();
  return kbId ? <ItemsList /> : <BasesList />;
}
